import { IconDefinition } from "@fortawesome/fontawesome-svg-core";
import { faHome } from "@fortawesome/pro-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { Location } from "history";
import React from "react";

import { Link } from "@/mk2/components/Link";
import { url } from "@/mk2/helpers/urls";
import {
    FORUM,
    REVIEW_SECTION_OFFER,
    STROLLER_QUESTIONS_AND_ANSWERS,
    STROLLER_REVIEWS,
} from "@/mk/autogenerated/translations/ReviewArticleTabs.ea735145004be3164d7038b57bb04d21";
import { ACTIVE_SERVER_ID } from "@/mk/settings";

import styles from "./ReviewArticleTabs.mscss";

export type Section =
    | "default"
    | "hodnotenia"
    | "hodnoceni"
    | "ponuka"
    | "nabidka"
    | "kde-kupim"
    | "kde-koupim"
    | "diskusie"
    | "diskuze"
    | "otazky"
    | "dotazy";

const VALID_SECTIONS: Section[] = [
    "default",
    "hodnotenia",
    "hodnoceni",
    "ponuka",
    "nabidka",
    "kde-kupim",
    "kde-koupim",
    "diskusie",
    "diskuze",
    "otazky",
    "dotazy",
];

export const normalizeSectionForInternal = (section: Section | null): Section => {
    if (!section) return "default";
    // Normalize CZ variants and legacy slugs to SK for internal use
    const normalized: Record<string, Section> = {
        hodnoceni: "hodnotenia",
        nabidka: "ponuka",
        "kde-kupim": "ponuka",
        "kde-koupim": "ponuka",
        diskuze: "diskusie",
        dotazy: "otazky",
    };
    return normalized[section] || (VALID_SECTIONS.includes(section) ? section : "default");
};

export const parseSection = (search: string): Section => {
    const params = new URLSearchParams(search);
    const s = params.get("s") as Section | null;
    return VALID_SECTIONS.includes(s) ? s : "default";
};

interface Tab {
    section: Section;
    label?: string;
    emoji?: IconDefinition;
    enabled: boolean;
}

interface OwnProps {
    location: Location;
    hasReviews: boolean;
    hasWhereToBuy: boolean;
    hasDiscussions: boolean;
    hasQuestionsAndAnswers: boolean;
}

type Props = OwnProps;

const ReviewArticleTabs: React.FC<Props> = ({
    location,
    hasReviews,
    hasWhereToBuy,
    hasDiscussions,
    hasQuestionsAndAnswers,
}) => {
    const isSK = ACTIVE_SERVER_ID === 201;
    const normalizedSection = normalizeSectionForInternal(parseSection(location.search));

    const getLocalizedSection = (internalSection: Section): Section => {
        if (isSK) {
            return internalSection as Section;
        }
        // Map SK internal sections to CZ URLs
        const czMap: Record<Section, Section> = {
            default: "default",
            hodnotenia: "hodnoceni",
            hodnoceni: "hodnoceni",
            ponuka: "nabidka",
            nabidka: "nabidka",
            "kde-kupim": "nabidka",
            "kde-koupim": "nabidka",
            diskusie: "diskuze",
            diskuze: "diskuze",
            otazky: "dotazy",
            dotazy: "dotazy",
        };
        return czMap[internalSection] || internalSection;
    };

    const tabs: Tab[] = [
        { section: "default", emoji: faHome, enabled: true },
        { section: "hodnotenia", label: STROLLER_REVIEWS, enabled: hasReviews },
        {
            section: "ponuka",
            label: REVIEW_SECTION_OFFER,
            enabled: hasWhereToBuy,
        },
        { section: "diskusie", label: FORUM, enabled: hasDiscussions },
        {
            section: "otazky",
            label: STROLLER_QUESTIONS_AND_ANSWERS,
            enabled: hasQuestionsAndAnswers,
        },
    ];

    const getUrl = (internalSection: Section): string => {
        return url(location.pathname, {}, { s: getLocalizedSection(internalSection) });
    };

    return (
        <nav className={styles.ReviewArticleTabs}>
            <ul className={styles.ReviewArticleTabs__list}>
                {tabs.map((tab) =>
                    tab.enabled ? (
                        <li key={tab.section} className={styles.ReviewArticleTabs__item}>
                            <Link
                                to={getUrl(tab.section)}
                                className={
                                    normalizedSection === tab.section
                                        ? styles["ReviewArticleTabs__link--active"]
                                        : styles.ReviewArticleTabs__link
                                }
                            >
                                {tab.emoji ? <FontAwesomeIcon icon={tab.emoji} /> : tab.label}
                            </Link>
                        </li>
                    ) : null,
                )}
            </ul>
        </nav>
    );
};

export default ReviewArticleTabs;
